import { RefObject, useContext, useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import {
    IFileFragment,
    IOfferEventSubscriptionStatus,
    UpdateOfferEventDocument,
    UpdateOfferDocument,
    OfferEventEnrollCurrentUserDocument,
    IOfferEventDetailFragment,
    IContentTypeValue,
    IOfferEventType,
    CurrentUserDocument,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useOfferEventDetail } from 'offer/hooks/useOfferEventDetail';
import { useApolloError } from 'common/hooks/useApolloError';
import { EOfferEventCategoryType, TOfferFormValues } from 'offer/types';
import { AlertDialog } from 'common/components/AlertDialog';
import { getGroupConditionMutationInputs } from 'user/utils/groups';
import { IGroupConditionInput } from 'user/types';
import { OfferPermissionsContext } from 'offer/contexts';
import { OfferEventPermissionsContext } from 'offer/contexts/OfferPermissionsContext';
import { Loader } from 'common/components/Loader';
import { useExtraCategoryValues } from 'common/hooks/useExtraCategoryValues';
import { Divider } from 'common/components/Divider';
import { OfferEventEnrollmentList } from 'offer/components/OfferEventEnrollmentList';
import { OfferEventManageableEnrollmentList } from 'offer/components/OfferEventManageableEnrollmentList';
import { OfferEventForm } from 'offer/forms/OfferEventForm';
import { BasicInfoSection } from 'offer/components/OfferEventDetail/BasicInfoSection';
import { DescriptionSection } from 'offer/components/OfferEventDetail/DescriptionSection';
import { ProgressSection } from 'offer/components/OfferEventDetail/ProgressSection';
import { SubscribeSection } from 'offer/components/OfferEventDetail/SubscribeSection';
import { UnsubscribeSection } from 'offer/components/OfferEventDetail/UnsubscribeSection';
import { Tab, Tabs } from 'common/components/Tabs';
import { Chip } from 'common/components/Chip';
import { getOfferEventStatusTranslation } from 'offer/utils/offerEvent';

import { SessionsSection } from './SessionsSection';
import { GroupConditionEnrollmentsSection } from './GroupConditionEnrollmentsSection';

interface IProps {
    drawerRef?: RefObject<HTMLDivElement | null>;
    edit?: boolean;
    includeOfferFields?: boolean;
    isOfferDetailDrawer?: boolean;
    offerEvent: IOfferEventDetailFragment;
    onCancelEdit?(): void;
    onClose?(cancelEdit?: boolean): void;
    onDelete?(): void;
}

export const OfferEventDetail = ({
    drawerRef,
    edit,
    includeOfferFields,
    isOfferDetailDrawer,
    offerEvent,
    onCancelEdit,
    onClose,
    onDelete,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [activeTab, setActiveTab] = useState(0);
    const [updateOffer, { loading: updateOfferLoading }] =
        useMutation(UpdateOfferDocument);
    const [updateOfferEvent, { loading: updateOfferEventLoading }] =
        useMutation(UpdateOfferEventDocument);
    const offerPermissions = useContext(OfferPermissionsContext);
    const offerEventPermissions = useContext(OfferEventPermissionsContext);
    const client = useApolloClient();
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const currentUser = currentUserData?.currentUser;

    const { canUpdate = false } = offerPermissions || {};
    const { canManageSubscriptions = false } = offerEventPermissions || {};

    const {
        title,
        offer: {
            id: offerId,
            extraCategoryValues,
            title: offerTitle,
            hasDetailPage,
            type: offerType,
            groupConditions,
        },
        trainerApproval,
        dates,
        descriptionAfterSubscription,
        type,
        cancelled,
        closingDateExpired,
    } = offerEvent;

    const {
        currentUserEnrollment,
        showSubscriptionStatusSection,
        showBasicInfoSection,
        showDescriptionSection,
        showSubscriptionsSection,
        showSubcribeSection,
        showUnsubscribeSection,
        canSubscribeViaItem,
        canSeeGroupConditions,
        showProgressSection,
        unenrollClosed,
    } = useOfferEventDetail({
        offerEvent,
    });
    const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
    const [enrollCurrentUser, { loading: enrollCurrentUserLoading }] =
        useMutation(OfferEventEnrollCurrentUserDocument);
    const { loading: extraCategoriesLoading, handleUpdateExtraCategoryValues } =
        useExtraCategoryValues({
            contentType: IContentTypeValue.Offer,
            modelId: offerId,
            typeNames: ['Offer'],
            skipQuery: !canUpdate || !edit,
        });

    const isEnrolled =
        !!currentUserEnrollment &&
        [
            IOfferEventSubscriptionStatus.Enrolled,
            IOfferEventSubscriptionStatus.Completed,
            IOfferEventSubscriptionStatus.Absent,
        ].includes(currentUserEnrollment.status);

    // Current user is trainer if they are assigned as trainer for the event or any of its dates
    const currentUserIsTrainer =
        !!offerEvent.trainers?.edges.some(
            (edge) => edge?.node?.id === currentUser?.id
        ) ||
        !!dates.some((date) =>
            date.trainers?.some((trainer) => trainer.id === currentUser?.id)
        );

    // Whether the user may click on material or date items
    const canAccessData =
        canUpdate ||
        canManageSubscriptions ||
        isEnrolled ||
        currentUserIsTrainer;

    const groupEnrollmentCounts = useMemo(() => {
        if (!canSeeGroupConditions) return [];

        return offerEvent.groupEnrollmentCounts.filter(
            (enrollmentCount) => !!enrollmentCount.max || !!enrollmentCount.min
        );
    }, [offerEvent.groupEnrollmentCounts, canSeeGroupConditions]);

    const offerEventStatus = getOfferEventStatusTranslation(
        offerEvent,
        canUpdate,
        canManageSubscriptions
    );

    const handleSubmit = async (
        values: TOfferFormValues & {
            overviewImage?: IFileFragment;
        }
    ) => {
        const {
            minSubscriptions,
            maxSubscriptions,
            offerEventTitle,
            certificate,
            ...otherValues
        } = values.event;

        if (includeOfferFields) {
            const {
                title,
                overviewImageId,
                extraCategories: extraCategoryUpdateValues,
                groupConditions,
            } = values.offer as TOfferFormValues['offer'] & {
                overviewImageId?: string;
            };

            try {
                await handleUpdateExtraCategoryValues(
                    extraCategoryValues,
                    extraCategoryUpdateValues
                );

                await updateOffer({
                    variables: {
                        id: offerId,
                        offer: {
                            title,
                            overviewImageId,
                            groupConditions: getGroupConditionMutationInputs(
                                (groupConditions as IGroupConditionInput[]) ||
                                    undefined
                            ),
                        },
                    },
                });
            } catch (error) {
                showApolloError(error);

                return;
            }
        }

        const offerEventValues = {
            ...otherValues,
            minSubscriptions,
            maxSubscriptions,
            title: offerEventTitle,
            certificateId: certificate?.id || null,
        };

        try {
            await updateOfferEvent({
                variables: {
                    id: offerEvent.id,
                    offerEvent: offerEventValues,
                },
                update: (cache) => {
                    onCancelEdit && onCancelEdit();

                    cache.evict({ fieldName: 'offerEvents' });
                    cache.evict({ fieldName: 'offers' });
                    cache.evict({ fieldName: 'paginatedOfferEvents' });
                    cache.evict({ id: `OfferEvent:${offerEvent.id}` });

                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate(
                includeOfferFields
                    ? 'offerActionSuccess.update'
                    : 'offerEventActionSuccess.update'
            ),
            {
                variant: 'success',
            }
        );

        onClose?.(true);

        return;
    };

    const handleEnroll = async (groupId?: string) => {
        await enrollCurrentUser({
            variables: { offerEventId: offerEvent.id, forGroupId: groupId },
            update: (cache) => {
                trainerApproval && setSubscribeDialogOpen(true);

                cache.evict({ fieldName: 'offerEvents' });
                cache.evict({ fieldName: 'paginatedOfferEvents' });

                cache.gc();
            },
        });
    };

    const offerEventCategoryType = !offerType
        ? EOfferEventCategoryType.Meeting
        : EOfferEventCategoryType.Offer;

    if (
        updateOfferEventLoading ||
        extraCategoriesLoading ||
        updateOfferLoading
    ) {
        return <Loader />;
    }

    if (edit && canUpdate) {
        return (
            <OfferEventForm
                edit
                includeOfferFields={includeOfferFields}
                isOfferDetailDrawer={isOfferDetailDrawer}
                offerEvent={offerEvent}
                offerEventCategoryType={offerEventCategoryType}
                offerEventType={offerEvent.type}
                onClose={onCancelEdit}
                onDelete={onDelete}
                onSubmit={handleSubmit}
            />
        );
    }

    const hasOfferEventTitle = !!title;

    const isTraining = type === IOfferEventType.Training;
    const isCourse = type === IOfferEventType.Course;
    const hasDates = !isTraining && !isCourse && !!dates.length;

    const showClosingDateExpiredBox =
        closingDateExpired &&
        !canUpdate &&
        !canManageSubscriptions &&
        !currentUserEnrollment;
    const showCanceledBox =
        cancelled &&
        !canUpdate &&
        !canManageSubscriptions &&
        !currentUserEnrollment;

    return (
        <Box
            sx={{
                px: { xs: 2, sm: 4 },
                pt: { xs: 2, sm: 3 },
                // The drawer already has some padding bottom so we need less here
                pb: { xs: 0, sm: 1 },
            }}
        >
            {canUpdate && offerEventStatus && (
                <Chip
                    color={offerEventStatus.color}
                    label={offerEventStatus.label}
                    sx={{ mb: 2 }}
                />
            )}

            {hasDetailPage && hasOfferEventTitle && (
                <Typography
                    sx={{
                        color: 'body.main',
                        fontSize: '1.6rem',
                        fontWeight: 700,
                    }}
                >
                    {offerTitle}
                </Typography>
            )}

            <Typography variant="h2">
                {hasOfferEventTitle ? title : offerTitle}
            </Typography>

            {showSubscriptionsSection && (
                <Tabs sx={{ mt: 1, mb: { xs: 2, sm: 3 } }} value={activeTab}>
                    <Tab
                        label={translate('offerEventDetail.tabs.info')}
                        value={0}
                        onClick={() => setActiveTab(0)}
                    />

                    <Tab
                        label={translate('offerEventDetail.tabs.enrollments')}
                        value={1}
                        onClick={() => setActiveTab(1)}
                    />
                </Tabs>
            )}

            {activeTab === 0 && (
                <Stack
                    useFlexGap
                    divider={<Divider />}
                    spacing={{ xs: 2, sm: 3 }}
                    sx={{
                        mt:
                            showSubscriptionsSection && showProgressSection
                                ? 0
                                : 3,
                    }}
                >
                    {showProgressSection && (
                        <ProgressSection offerEvent={offerEvent} />
                    )}
                    {showBasicInfoSection && (
                        <BasicInfoSection
                            groupConditions={
                                canSeeGroupConditions
                                    ? groupConditions || undefined
                                    : undefined
                            }
                            offerEvent={offerEvent}
                            showSubscriptionStatusSection={
                                !offerEventStatus &&
                                showSubscriptionStatusSection
                            }
                        />
                    )}
                    {hasDates && (
                        <SessionsSection
                            canAccessData={canAccessData}
                            dates={dates}
                            descriptionAfterSubscription={
                                descriptionAfterSubscription
                            }
                            offerTitle={offerTitle}
                        />
                    )}

                    {showDescriptionSection && (
                        <DescriptionSection
                            canAccessData={canAccessData}
                            canUpdate={canUpdate}
                            currentUserEnrollment={currentUserEnrollment}
                            offerEvent={offerEvent}
                            onClickItem={async (itemCallback) => {
                                // When user can subscribe via item we call the subscribe function
                                if (
                                    canSubscribeViaItem &&
                                    !isEnrolled &&
                                    !canUpdate
                                ) {
                                    await handleEnroll();

                                    // Trigger a refetch of the offer event after the enroll en item callback
                                    client.cache.evict({
                                        id: client.cache.identify(offerEvent),
                                        broadcast: true,
                                    });
                                }

                                await itemCallback?.();

                                return;
                            }}
                        />
                    )}

                    {(showClosingDateExpiredBox || showCanceledBox) && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2,
                                borderRadius: 1,
                                bgcolor: 'error.bgColor',
                                color: 'error.dark',
                            }}
                        >
                            <Typography>
                                {translate(
                                    cancelled
                                        ? 'offerEventDetail.canceledMessage'
                                        : 'offerEventDetail.subscriptionsClosedMessage'
                                )}
                            </Typography>
                        </Box>
                    )}

                    {showSubcribeSection && (
                        <SubscribeSection
                            canManageParticipants={canManageSubscriptions}
                            loading={enrollCurrentUserLoading}
                            offerEvent={offerEvent}
                            onClickSubscribe={async (groupId?: string) => {
                                await handleEnroll(groupId);

                                // Trigger a refetch of the offer event
                                client.cache.evict({
                                    id: client.cache.identify(offerEvent),
                                    broadcast: true,
                                });
                            }}
                        />
                    )}

                    {showUnsubscribeSection && (
                        <Box
                            sx={[
                                showSubscriptionsSection
                                    ? { mt: 1 }
                                    : { mt: 0 },
                            ]}
                        >
                            <UnsubscribeSection
                                canManageParticipants={canManageSubscriptions}
                                offerEvent={offerEvent}
                                unenrollClosed={unenrollClosed}
                                onSubmit={onClose}
                            />
                        </Box>
                    )}
                </Stack>
            )}

            {activeTab === 1 && showSubscriptionsSection && (
                <Stack
                    useFlexGap
                    divider={<Divider />}
                    spacing={{ xs: 2, sm: 3 }}
                >
                    {!!groupEnrollmentCounts.length && (
                        <GroupConditionEnrollmentsSection
                            groupEnrollmentCounts={groupEnrollmentCounts}
                        />
                    )}

                    <Box>
                        {canManageSubscriptions || canUpdate ? (
                            // Show the enrollment list for users who can manage the subscriptions
                            <OfferEventManageableEnrollmentList
                                drawerRef={drawerRef}
                                offerEvent={offerEvent}
                            />
                        ) : (
                            // Show the enrollment list for participants
                            <OfferEventEnrollmentList
                                disableClickRows={!currentUserEnrollment}
                                offerEvent={offerEvent}
                            />
                        )}
                    </Box>
                </Stack>
            )}

            <AlertDialog
                actions={
                    <Box sx={{ mt: 2 }}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => {
                                setSubscribeDialogOpen(false);
                            }}
                        >
                            {translate('close')}
                        </Button>
                    </Box>
                }
                open={subscribeDialogOpen}
                title={translate('offerEventSubscribe.dialogTitle')}
            >
                {translate('offerEventSubscribe.dialogText')}
            </AlertDialog>
        </Box>
    );
};
